import React, { useState, useEffect } from 'react';
import './login.css';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../../stores/store';
import { login } from "../../stores/store";
import logo from './arqite-logo.svg';
import ApiService from "../../services/apiService";
import OAuthService from '../../services/oauthService';import OrganizationService from '../../services/organizationService';import { useToast } from '../../components/toast/toastProvider';
import HealthIndicator from '../../components/health-indicator/HealthIndicator';
import { 
  FiMail, 
  FiLock, 
  FiEye, 
  FiEyeOff, 
  FiShield, 
  FiUsers, 
  FiCheckCircle,
  FiAlertCircle,
  FiLoader
} from 'react-icons/fi';

interface AuthFormData {
  organizationId?: string;
  username: string;
  email: string;
  password: string;
  confirmPassword?: string;
}


const LoginPage: React.FC = () => {
  const [isLogin, setIsLogin] = useState(true);
  const dispatch = useDispatch<AppDispatch>();
  const { addToast } = useToast();
  const [organizations, setOrganizations] = useState<{id: string; name: string}[]>([]);
  const [loadingOrgs, setLoadingOrgs] = useState(false);

  const [formData, setFormData] = useState<AuthFormData>({
    organizationId: '',
    email: '',
    username: '',
    password: '',
    confirmPassword: ''
  });

  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [oauthLoading, setOauthLoading] = useState<'microsoft' | 'google' | null>(null);
  const [rememberMe, setRememberMe] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [emailValid, setEmailValid] = useState(false);

  // Initialize OAuth providers and load organizations
  useEffect(() => {
    OAuthService.initializeProviders();
    loadOrganizations();
  }, []);

  const loadOrganizations = async () => {
    setLoadingOrgs(true);
    try {
      const response = await OrganizationService.getOrganizations();
      const orgs = response.orgs || [];
      
      // If no organizations from API, use default fallback
      setOrganizations(orgs.length > 0 ? orgs : [
        { id: '1', name: 'John Sisk & Son' },
        { id: '2', name: 'Glenveagh' },
        { id: '3', name: 'QBE MCI' },
        { id: '4', name: 'Mercury Engineering' },
        { id: '5', name: 'BAM Ireland' },
        { id: '6', name: 'JK Williams' }
      ]);
    } catch (error) {
      console.error('Failed to load organizations:', error);
      // Fallback to default organizations on error
      setOrganizations([
        { id: '1', name: 'John Sisk & Son' },
        { id: '2', name: 'Glenveagh' },
        { id: '3', name: 'QBE MCI' },
        { id: '4', name: 'Mercury Engineering' },
        { id: '5', name: 'BAM Ireland' },
        { id: '6', name: 'JK Williams' }
      ]);
    } finally {
      setLoadingOrgs(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Validate email
    if (name === 'email') {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      setEmailValid(emailRegex.test(value));
    }
    
    // Calculate password strength
    if (name === 'password') {
      setPasswordStrength(calculatePasswordStrength(value));
    }
  };

  const calculatePasswordStrength = (password: string): number => {
    let strength = 0;
    if (password.length >= 8) strength += 1;
    if (password.length >= 12) strength += 1;
    if (/[a-z]/.test(password)) strength += 1;
    if (/[A-Z]/.test(password)) strength += 1;
    if (/[0-9]/.test(password)) strength += 1;
    if (/[^A-Za-z0-9]/.test(password)) strength += 1;
    return strength;
  };

  const getPasswordStrengthColor = (strength: number): string => {
    if (strength <= 2) return '#ef4444';
    if (strength <= 4) return '#f59e0b';
    return '#10b981';
  };

  const getPasswordStrengthText = (strength: number): string => {
    if (strength <= 2) return 'Weak';
    if (strength <= 4) return 'Medium';
    return 'Strong';
  };

  // OAuth Login Methods
  const handleMicrosoftLogin = async () => {
    setOauthLoading('microsoft');
    try {
      const { user, tokens } = await OAuthService.loginWithMicrosoft();
      
      // Store tokens securely
      const activeStorage = rememberMe ? localStorage : sessionStorage;
      const inactiveStorage = rememberMe ? sessionStorage : localStorage;
      inactiveStorage.removeItem('oauth_tokens');
      inactiveStorage.removeItem('authUser');
      if (rememberMe) {
        activeStorage.setItem('oauth_tokens', JSON.stringify(tokens));
      } else {
        activeStorage.setItem('oauth_tokens', JSON.stringify(tokens));
      }
      activeStorage.setItem('authUser', user.name);
      
      dispatch(login(user.name));
      addToast({
        type: 'success',
        title: 'Welcome!',
        message: `Successfully signed in with Microsoft as ${user.name}`,
        duration: 4000
      });
    } catch (error: any) {
      addToast({
        type: 'error',
        title: 'Microsoft Sign-In Failed',
        message: error.message || 'Failed to authenticate with Microsoft',
        duration: 5000
      });
    } finally {
      setOauthLoading(null);
    }
  };

  const handleGoogleLogin = async () => {
    setOauthLoading('google');
    try {
      const { user, tokens } = await OAuthService.loginWithGoogle();
      
      // Store tokens securely
      const activeStorage = rememberMe ? localStorage : sessionStorage;
      const inactiveStorage = rememberMe ? sessionStorage : localStorage;
      inactiveStorage.removeItem('oauth_tokens');
      inactiveStorage.removeItem('authUser');
      if (rememberMe) {
        activeStorage.setItem('oauth_tokens', JSON.stringify(tokens));
      } else {
        activeStorage.setItem('oauth_tokens', JSON.stringify(tokens));
      }
      activeStorage.setItem('authUser', user.name);
      
      dispatch(login(user.name));
      addToast({
        type: 'success',
        title: 'Welcome!',
        message: `Successfully signed in with Google as ${user.name}`,
        duration: 4000
      });
    } catch (error: any) {
      addToast({
        type: 'error',
        title: 'Google Sign-In Failed',
        message: error.message || 'Failed to authenticate with Google',
        duration: 5000
      });
    } finally {
      setOauthLoading(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      if (isLogin) {
        // Login validation
        if (!formData.email?.trim()) {
          addToast({
            type: 'error',
            title: 'Missing Email',
            message: 'Please enter your email address',
            duration: 5000
          });
          setIsLoading(false);
          return;
        }

        if (!formData.password?.trim()) {
          addToast({
            type: 'error',
            title: 'Missing Password',
            message: 'Please enter your password',
            duration: 5000
          });
          setIsLoading(false);
          return;
        }

        // Login API call
        const response = await ApiService.login(formData.email, formData.password);
        const username = response?.user?.username || formData.email;

        if (response?.arqiteToken) {
          const activeStorage = rememberMe ? localStorage : sessionStorage;
          const inactiveStorage = rememberMe ? sessionStorage : localStorage;
          inactiveStorage.removeItem('authToken');
          inactiveStorage.removeItem('authUser');
          activeStorage.setItem('authToken', response.arqiteToken);
          activeStorage.setItem('authUser', username);
        }
        
        dispatch(login(username));
        addToast({
          type: 'success',
          title: 'Welcome back!',
          message: `Successfully signed in as ${username}`,
          duration: 4000
        });

      } else {
        // Signup validation
        if (!formData.organizationId?.trim()) {
          addToast({
            type: 'error',
            title: 'Missing Organization',
            message: 'Please select an organization',
            duration: 5000
          });
          setIsLoading(false);
          return;
        }

        if (!formData.username?.trim()) {
          addToast({
            type: 'error',
            title: 'Missing Username',
            message: 'Please enter a username',
            duration: 5000
          });
          setIsLoading(false);
          return;
        }

        if (!formData.email?.trim()) {
          addToast({
            type: 'error',
            title: 'Missing Email',
            message: 'Please enter an email address',
            duration: 5000
          });
          setIsLoading(false);
          return;
        }

        if (formData.password !== formData.confirmPassword) {
          addToast({
            type: 'error',
            title: 'Password Mismatch',
            message: 'Please make sure your passwords match',
            duration: 5000
          });
          setIsLoading(false);
          return;
        }

        if (passwordStrength < 3) {
          addToast({
            type: 'error',
            title: 'Weak Password',
            message: 'Your password must be at least medium strength',
            duration: 5000
          });
          setIsLoading(false);
          return;
        }

        // Signup API call
        const response = await ApiService.signup(
          formData.email,
          formData.password,
          formData.organizationId,
          formData.username
        );
        
        const createdUsername = response?.user?.username || formData.username || formData.email;
        
        addToast({
          type: 'success',
          title: 'Account Created!',
          message: `Your account has been created successfully with username "${createdUsername}". You can now sign in.`,
          duration: 6000
        });

        // Switch to login mode after successful signup
        setTimeout(() => {
          setIsLogin(true);
          setFormData(prev => ({
            ...prev,
            organizationId: '',
            username: '',
            confirmPassword: ''
          }));
        }, 1500);
      }
    } catch (error: any) {
      // Handle error with toast
      let errorMessage = 'Something went wrong. Please try again.';
      let errorTitle = isLogin ? 'Sign In Failed' : 'Sign Up Failed';

      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      addToast({
        type: 'error',
        title: errorTitle,
        message: errorMessage,
        duration: 6000,
        action: {
          label: 'Retry',
          onClick: () => handleSubmit(e)
        }
      });
    }

    setIsLoading(false);
  };

  return (
    <div className="arqite-container">
      {/* Left Side - Branding */}
      <div className="arqite-brand-side">
        <div className="geometric-pattern">
          <div className="floating-shape shape-1"></div>
          <div className="floating-shape shape-2"></div>
          <div className="floating-shape shape-3"></div>
          <div className="floating-shape shape-4"></div>
          <div className="floating-shape shape-5"></div>
        </div>
        
        <div className="brand-content">
          <div className="brand-logo">
            <img className="logo-icon" src={logo} alt="Arqite" />
            <span>ARQITE</span>
          </div>
          
          <div className="brand-text">
            <h2>Quality Management,<br />Reinvented.</h2>
            <p>Enterprise-Grade Control for Project Quality.</p>
          </div>
          
          <div className="feature-highlights">
            <div className="feature">
              <div className="feature-icon">⚡</div>
              <span>Lightning Fast</span>
            </div>
            <div className="feature">
              <div className="feature-icon">🔒</div>
              <span>Secure by Design</span>
            </div>
            <div className="feature">
              <div className="feature-icon">🚀</div>
              <span>Future Ready</span>
            </div>
          </div>
        </div>
      </div>

      {/* Right Side - Form */}
      <div className="arqite-form-side">
        <div className="form-container">
          <div className="form-header">
            <div className="header-top">
              <h1>{isLogin ? 'Welcome Back' : 'Get Started'}</h1>
              <HealthIndicator />
            </div>
            <p>{isLogin ? 'Access your ARQITE dashboard' : 'Create your account in seconds'}</p>
          </div>

          <div className="mode-switcher">
            <div className="switcher-track">
              <div className={`switcher-thumb ${isLogin ? 'login' : 'signup'}`}></div>
              <button 
                className={`mode-btn ${isLogin ? 'active' : ''}`}
                onClick={() => setIsLogin(true)}
              >
                Sign In
              </button>
              <button 
                className={`mode-btn ${!isLogin ? 'active' : ''}`}
                onClick={() => setIsLogin(false)}
              >
                Sign Up
              </button>
            </div>
          </div>

          {/* Enterprise Login Options */}
          <div className="enterprise-login">
            <div className="enterprise-header">
              <FiShield className="enterprise-icon" />
              <h3>Company Sign-In</h3>
            </div>
            
            <div className="oauth-buttons">
              <button
                type="button"
                className={`oauth-btn microsoft ${oauthLoading === 'microsoft' ? 'loading' : ''}`}
                onClick={handleMicrosoftLogin}
                disabled={oauthLoading !== null}
              >
                {oauthLoading === 'microsoft' ? (
                  <FiLoader className="spinning" />
                ) : (
                  <svg className="microsoft-icon" viewBox="0 0 24 24" width="20" height="20">
                    <path fill="#00a4ef" d="M0 0h11.377v11.372H0z"/>
                    <path fill="#7fba00" d="M12.623 0H24v11.372H12.623z"/>
                    <path fill="#ffb900" d="M0 12.628h11.377V24H0z"/>
                    <path fill="#f25022" d="M12.623 12.628H24V24H12.623z"/>
                  </svg>
                )}
                <span>Sign in with Outlook</span>
              </button>

              <button
                type="button"
                className={`oauth-btn google ${oauthLoading === 'google' ? 'loading' : ''}`}
                onClick={handleGoogleLogin}
                disabled={oauthLoading !== null}
              >
                {oauthLoading === 'google' ? (
                  <FiLoader className="spinning" />
                ) : (
                  <svg className="google-icon" viewBox="0 0 24 24" width="20" height="20">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                )}
                <span>Sign in with Google</span>
              </button>
            </div>
          </div>

          <div className="separator">
            <span>or continue with email</span>
          </div>

          <form onSubmit={handleSubmit} className="modern-form">
            {!isLogin && (
              <div className="input-wrapper">
                <select
                  name="organizationId"
                  value={formData.organizationId}
                  onChange={handleInputChange}
                  required
                  disabled={loadingOrgs}
                  className="modern-input"
                >
                  <option value="" disabled>{loadingOrgs ? 'Loading...' : 'Select Organization'}</option>
                  {organizations.map(org => (
                    <option key={org.id} value={org.id}>{org.name}</option>
                  ))}
                </select>
                <label className="floating-label">Organization</label>
                <FiUsers className="input-icon" />
              </div>
            )}
            
            {!isLogin && (
              <div className="input-wrapper">
                <input
                  type="text"
                  name="username"
                  placeholder=" "
                  value={formData.username}
                  onChange={handleInputChange}
                  required
                  className="modern-input"
                />
                <label className="floating-label">User name</label>
                <FiUsers className="input-icon" />
              </div>
            )}
            
            <div className="input-wrapper">
              <input
                type="email"
                name="email"
                placeholder=" "
                value={formData.email}
                onChange={handleInputChange}
                required
                className={`modern-input ${formData.email && !emailValid ? 'error' : ''} ${formData.email && emailValid ? 'valid' : ''}`}
              />
              <label className="floating-label">Email Address</label>
              <FiMail className="input-icon" />
              {formData.email && (
                <div className="validation-icon">
                  {emailValid ? <FiCheckCircle className="valid" /> : <FiAlertCircle className="error" />}
                </div>
              )}
            </div>

            <div className="input-wrapper">
              <input
                type={showPassword ? "text" : "password"}
                name="password"
                placeholder=" "
                value={formData.password}
                onChange={handleInputChange}
                required
                className="modern-input"
              />
              <label className="floating-label">Password</label>
              <FiLock className="input-icon" />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <FiEyeOff /> : <FiEye />}
              </button>
               {!isLogin && formData.password && (
                 <div className="password-strength">
                   <div className="strength-bar">
                     <div 
                       className="strength-fill" 
                       style={{ 
                         width: `${(passwordStrength / 6) * 100}%`,
                         backgroundColor: getPasswordStrengthColor(passwordStrength)
                       }}
                     ></div>
                   </div>
                   <span className="strength-text" style={{ color: getPasswordStrengthColor(passwordStrength) }}>
                     {getPasswordStrengthText(passwordStrength)}
                   </span>
                 </div>
               )}
            </div>

            {!isLogin && (
              <div className="input-wrapper">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  name="confirmPassword"
                  placeholder=" "
                  value={formData.confirmPassword || ''}
                  onChange={handleInputChange}
                  required={!isLogin}
                  className="modern-input"
                />
                <label className="floating-label">Confirm Password</label>
                <FiLock className="input-icon" />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? <FiEyeOff /> : <FiEye />}
                </button>
              </div>
            )}

            <div className="form-options">
              <label className="checkbox-wrapper">
                <input 
                  type="checkbox" 
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                />
                <span className="checkmark"></span>
                Remember me
              </label>
              {isLogin && (
                <a href="#forgot" className="forgot-link">Forgot password?</a>
              )}
            </div>

            <button 
              type="submit" 
              className={`submit-btn ${isLoading ? 'loading' : ''}`}
              disabled={
                isLoading || 
                (!isLogin && (
                  !formData.organizationId?.trim() ||
                  !formData.username?.trim() ||
                  !formData.email?.trim() ||
                  !formData.password?.trim() ||
                  !formData.confirmPassword?.trim() ||
                  passwordStrength < 3 ||
                  !emailValid
                )) ||
                (isLogin && (
                  !formData.email?.trim() ||
                  !formData.password?.trim() ||
                  !emailValid
                ))
              }
            >
              <span className="btn-text">
                {isLoading ? '' : (isLogin ? 'Sign In' : 'Create Account')}
              </span>
              {isLoading && <div className="loading-spinner"></div>}
            </button>
          </form>

          <div className="form-footer">
            <p>
              {isLogin ? "Don't have an account? " : "Already have an account? "}
              <button 
                className="switch-link"
                onClick={() => setIsLogin(!isLogin)}
              >
                {isLogin ? 'Sign up for free' : 'Sign in instead'}
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
